import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import type { ModuleKey } from '@/permissions';

/**
 * Returns reactive role + allowed-module references plus two predicates:
 *  - `can(module)`        – does the user have access to a single module?
 *  - `canAny(modules[])`  – does the user have access to ANY of the modules?
 */
export function usePermissions() {
    const authStore = useAuthStore();
    const { roleName, allowedModules, isAuthenticated, hasAnyModuleAccess } =
        storeToRefs(authStore);

    const allowedModuleSet = computed(() => new Set(allowedModules.value));

    const can = (module: ModuleKey): boolean => allowedModuleSet.value.has(module);

    const canAny = (modules: ModuleKey[]): boolean =>
        modules.some((m) => allowedModuleSet.value.has(m));

    return {
        roleName,
        allowedModules,
        isAuthenticated,
        hasAnyModuleAccess,
        can,
        canAny,
    };
}
